import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DeleteFilled, DownOutlined, FileTextOutlined } from '@ant-design/icons'
import {
    Button,
    Dropdown,
    Empty,
    Flex,
    FloatButton,
    message,
    Modal,
    Popconfirm,
} from 'antd'
import { getMarcRecord } from 'api/rest/descriptorsRequests'
import { Link } from 'gatsby'
import { useClipboardStorage } from 'hooks/useClipboardStorage/useClipboardStorage'
import fileDownload from 'js-file-download'
import { DownloadFormat } from 'records'
import { IClipboardItem } from 'store/clipboardStore/clipboardStore'
import {
    downloadFormatDropdownItems,
    downloadFormats,
} from 'utils/download-formats'

import { handleDownloadJson } from './handleDownloadJson'
import { handleDownloadMrc } from './handleDownloadMrc'
import { handleDownloadXml } from './handleDownloadXml'

function splitByRelatedDescriptorUi(items: IClipboardItem[]) {
    const qualifiersList = []
    const expandedTermsList = []

    for (const item of items) {
        if ('relatedDescriptorUi' in item) {
            expandedTermsList.push(item)
        } else {
            qualifiersList.push(item)
        }
    }

    return { qualifiersList, expandedTermsList }
}

export default function Clipboard() {
    const { t, i18n } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [format, setFormat] = useState<DownloadFormat>(downloadFormats.xml)
    const [messageApi, contextHolder] = message.useMessage()

    const successMessage = () => {
        messageApi.open({
            type: 'success',
            content: t('download_success'),
        })
    }

    const errorMessage = () => {
        messageApi.open({
            type: 'error',
            content: t('error_download_data'),
        })
    }

    const {
        items: descriptors,
        clearItems: clearDescriptors,
        removeItem: removeDescriptor,
    } = useClipboardStorage('descriptors')
    const {
        items: qualifiers,
        clearItems: clearQualifiers,
        removeItem: removeQualifier,
    } = useClipboardStorage('qualifiers')

    const { qualifiersList, expandedTermsList } =
        splitByRelatedDescriptorUi(qualifiers)

    const showModal = () => {
        setIsModalOpen(true)
    }

    const closeModal = () => {
        setIsModalOpen(false)
    }

    const getFileBlob = async (blobs: Blob[]): Promise<Blob> => {
        switch (format.format) {
            case downloadFormats.ISO_2709.format: {
                return handleDownloadMrc(blobs)
            }
            case downloadFormats.xml.format: {
                return await handleDownloadXml(blobs)
            }
            case downloadFormats.json.format: {
                return await handleDownloadJson(blobs)
            }
            default:
                throw new Error('Invalid format')
        }
    }

    const downloadMARC = async () => {
        try {
            const qualifiersPromise = Promise.all([
                ...qualifiers.map((q) =>
                    q.relatedDescriptorUi
                        ? getMarcRecord(
                              `marc/expanded/${q.relatedDescriptorUi}/${q.ui}`,
                              {
                                  year: q.selectedYear,
                                  format: format.format,
                              }
                          )
                        : getMarcRecord(`marc/qualifiers/${q.ui}`, {
                              year: q.selectedYear,
                              format: format.format,
                          })
                ),
            ])

            const descriptorsPromise = Promise.all([
                ...descriptors.map((d) =>
                    getMarcRecord(`marc/descriptors/${d.ui}`, {
                        year: d.selectedYear,
                        format: format.format,
                    })
                ),
            ])

            const [qualifiersResponse, descriptorsResponse] = await Promise.all(
                [qualifiersPromise, descriptorsPromise]
            )

            const fileBlob = await getFileBlob([
                ...qualifiersResponse,
                ...descriptorsResponse,
            ])

            fileDownload(fileBlob, `MARC.${format.extension || format.format}`)
            successMessage()
        } catch {
            errorMessage()
        }
    }

    const hasNoData = !descriptors.length && !qualifiers.length

    return (
        <div>
            {contextHolder}
            <FloatButton
                icon={<FileTextOutlined />}
                aria-label={t('clipboard')}
                onClick={showModal}
            />
            <Modal
                title={t('clipboard')}
                open={isModalOpen}
                onOk={closeModal}
                onCancel={closeModal}
                footer={[
                    <Flex gap="small" justify="flex-end" key="1">
                        <Dropdown.Button
                            key="1"
                            onClick={downloadMARC}
                            menu={{
                                items: downloadFormatDropdownItems,
                                onClick: (e) =>
                                    setFormat(downloadFormats[e.key]),
                                selectable: true,
                            }}
                            icon={<DownOutlined />}
                            style={{ width: 'auto' }}
                            disabled={hasNoData}
                        >
                            {t('download_MARC')}
                        </Dropdown.Button>

                        <Popconfirm
                            title={t('really_clear_the_clipboard')}
                            onConfirm={() => {
                                clearQualifiers()
                                clearDescriptors()
                            }}
                            okText={t('clear')}
                            cancelText={t('cancel')}
                            disabled={hasNoData}
                        >
                            <Button disabled={hasNoData} type="default" danger>
                                {t('clear')}
                            </Button>
                        </Popconfirm>
                    </Flex>,
                ]}
            >
                <Flex vertical gap="large">
                    <Flex vertical gap="small">
                        {descriptors.length ? <b>{t('descriptors')}</b> : null}
                        {descriptors.map((d) => (
                            <Flex key={d.ui} justify="space-between">
                                <Link
                                    to={`/qualifiers/${
                                        d.ui
                                    }-${i18n.language.toUpperCase()}?year=${
                                        d.selectedYear
                                    }`}
                                    onClick={closeModal}
                                >
                                    {d.ui} - {d.recordName} ({d.selectedYear})
                                </Link>
                                <Button
                                    type="default"
                                    icon={<DeleteFilled />}
                                    onClick={() => removeDescriptor(d.ui)}
                                    danger
                                    size="small"
                                    style={{ height: '24px' }}
                                />
                            </Flex>
                        ))}
                    </Flex>
                    <Flex vertical gap="small">
                        {qualifiersList.length ? (
                            <b>{t('qualifiers')}</b>
                        ) : null}
                        {qualifiersList.map((q) => (
                            <Flex key={q.ui} justify="space-between">
                                <Link
                                    to={`/qualifiers/${
                                        q.ui
                                    }-${i18n.language.toUpperCase()}?year=${
                                        q.selectedYear
                                    }`}
                                    onClick={closeModal}
                                >
                                    {q.ui} - {q.recordName} ({q.selectedYear})
                                </Link>
                                <Button
                                    type="default"
                                    icon={<DeleteFilled />}
                                    onClick={() => removeQualifier(q.ui)}
                                    danger
                                    size="small"
                                    style={{ height: '24px' }}
                                />
                            </Flex>
                        ))}
                    </Flex>
                    <Flex vertical gap="small">
                        {expandedTermsList.length ? (
                            <b>{t('extended_records')}</b>
                        ) : null}
                        {expandedTermsList.map((q) => (
                            <Flex key={q.ui} justify="space-between">
                                <Link
                                    to={`/qualifiers/${
                                        q.ui
                                    }-${i18n.language.toUpperCase()}?year=${
                                        q.selectedYear
                                    }`}
                                    onClick={closeModal}
                                >
                                    {q.ui} ({q.relatedDescriptorUi}) -{' '}
                                    {q.recordName} ({q.selectedYear})
                                </Link>
                                <Button
                                    type="default"
                                    icon={<DeleteFilled />}
                                    onClick={() => removeQualifier(q.ui)}
                                    danger
                                    size="small"
                                    style={{ height: '24px' }}
                                />
                            </Flex>
                        ))}
                    </Flex>

                    {hasNoData ? (
                        <Empty description={t('no_data_in_the_clipboard')} />
                    ) : null}
                </Flex>
            </Modal>
        </div>
    )
}
